import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import syncStyles from './syncStyles';

/**
 * ViewportFrame — renders children inside an iframe of a given CSS width.
 *
 * Components whose layout switches on `@media` read the VIEWPORT, not their
 * own box. Rendering the Header inside the dashboard's ~1020px content column
 * therefore gave it the ≥1360px centred-logo layout squeezed into 1020px — the
 * logo collided with the nav, a combination production never produces.
 *
 * An iframe has its own viewport, so `@media (min-width: 1360px)` resolves
 * against `width` here and the preview matches the real page exactly. The
 * frame is then scaled down with a transform to fit the card, which does not
 * affect the media queries.
 */

/** Host styles, plus the cosmetics a visible preview needs. */
const syncPreviewStyles = (frameDoc) => {
  syncStyles(frameDoc);
  frameDoc.body.style.margin = '0';
  frameDoc.body.style.background = 'var(--color-gray-0)';
};

const ViewportFrame = ({ width = 1440, height = 120, label, children }) => {
  const frameRef = useRef(null);
  const wrapRef = useRef(null);
  const [body, setBody] = useState(null);
  const [scale, setScale] = useState(1);

  const attach = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return false;
    syncPreviewStyles(doc);
    setBody(doc.body);
    return true;
  }, []);

  /**
   * A srcless same-origin iframe has its document ready as soon as it is in
   * the DOM, and fires `load` before React can attach a handler — so attach
   * from an effect instead. The rAF covers the browsers that need one tick.
   */
  useEffect(() => {
    if (attach()) return undefined;
    const raf = requestAnimationFrame(attach);
    return () => cancelAnimationFrame(raf);
  }, [attach]);

  // Re-copy styles when the brand flips or dev-server HMR injects new ones.
  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc || !body) return undefined;

    const observer = new MutationObserver(() => syncPreviewStyles(doc));
    observer.observe(document.head, { childList: true, subtree: true });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-brand'],
    });
    return () => observer.disconnect();
  }, [body]);

  // Scale the frame down to fit the card. Transform scaling leaves the
  // iframe's internal viewport at `width`, so media queries are unaffected.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const measure = () => {
      const available = wrap.clientWidth;
      setScale(available > 0 ? Math.min(1, available / width) : 1);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div>
      <div ref={wrapRef} style={{ height: height * scale, overflow: 'hidden' }}>
        <iframe
          ref={frameRef}
          onLoad={attach}
          title={label || `Preview at ${width}px`}
          style={{
            width,
            height,
            border: 0,
            display: 'block',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
        {body && createPortal(children, body)}
      </div>
      <span
        className="typo-label"
        style={{ display: 'block', paddingTop: 'var(--space-ui-small-l)', color: 'var(--ds-doc-ink-muted)' }}
      >
        {width}px viewport{scale < 1 ? ` · shown at ${Math.round(scale * 100)}%` : ''}
      </span>
    </div>
  );
};

ViewportFrame.propTypes = {
  /** CSS viewport width the frame reports to media queries */
  width: PropTypes.number,
  /** Frame height in px */
  height: PropTypes.number,
  /** Accessible title for the iframe */
  label: PropTypes.string,
  children: PropTypes.node,
};

export default ViewportFrame;
